import React, { useEffect, useState } from "react";
import Layout from "../../../layouts/default";
import { useLocation, useParams } from "react-router-dom";
import { Button, Table, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import PermissionList from "../../../data/permissions.json";
import { useDispatch } from "react-redux";
import {
    fetchAllPermissions,
    savePermissions
} from "../../../store/slices/rolesAndPermissions/rolesAndPermissions";
import { hasPermission } from "../../../services/utils";
import { PERMISSIONS } from "../../../contants/permission";
import { FcOk } from "react-icons/fc";
import { AiFillCloseCircle } from "react-icons/ai";

const ACTIONS = ["view", "create", "edit", "delete", "download"];

function PermissionManagement() {
    const { role_id } = useParams();
    const { state } = useLocation();
    const role = state?.role;

    const dispatch = useDispatch();
    const [permissions, setPermissions] = useState([]);
    const [existingPermissions, setExistingPermissions] = useState([]);
    const [loading, setLoading] = useState(false);

    const modules = [...PermissionList];

    useEffect(() => {
        if (!role_id) return;
        loadPermissions(Number(role_id));
    }, [role_id]);

    const loadPermissions = async (roleId) => {
        try {
            setLoading(true);
            const res = await dispatch(fetchAllPermissions({ roleId })).unwrap();
            if (res?.success && Array.isArray(res.data)) {
                const flat = convertDbToFlat(res.data);
                setPermissions(flat);
                setExistingPermissions(res.data);
            } else {
                toast.error(res?.message || "Something went wrong.");
            }
        } catch {
            toast.error("Failed to load permissions");
        } finally {
            setLoading(false);
        }
    };

    const convertDbToFlat = (dbPerms) => {
        let flat = [];
        dbPerms.forEach((perm) => {
            perm.actions.forEach((action) => {
                flat.push({
                    module: perm.module,
                    action,
                    id: perm.id
                });
            });
        });
        return flat;
    };

    const isChecked = (module, action) =>
        permissions.some((p) => p.module === module && p.action === action);

    const togglePermission = (module, action) => {
        // Only allow toggle if user has permission
        if (!(hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) &&
            hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT))) {
            return;
        }

        setPermissions((prev) => {
            const exists = prev.some(
                (p) => p.module === module && p.action === action
            );

            if (exists) {
                return prev.filter((p) => !(p.module === module && p.action === action));
            } else {
                const existing = existingPermissions.find(ep => ep.module === module);
                return [...prev, { module, action, id: existing?.id || undefined }];
            }
        });
    };

    const selectAllForModule = (module, actions) => {
        if (!(hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) &&
            hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT))) {
            return;
        }

        const existing = existingPermissions.find(p => p.module === module);

        setPermissions((prev) => {
            const filtered = prev.filter((p) => p.module !== module);
            const add = actions.map((a) => ({
                module,
                action: a,
                id: existing?.id || undefined
            }));
            return [...filtered, ...add];
        });
    };

    const unselectAllForModule = (module) => {
        if (!(hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) &&
            hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT))) {
            return;
        }

        setPermissions((prev) => prev.filter((p) => p.module !== module));
    };

    const selectAllGlobal = () => {
        if (!(hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) &&
            hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT))) {
            return;
        }

        const all = [];
        modules.forEach((m) => {
            const existing = existingPermissions.find(ep => ep.module === m.module);
            m.actions.forEach((a) => {
                all.push({
                    module: m.module,
                    action: a,
                    id: existing?.id || undefined
                });
            });
        });
        setPermissions(all);
    };

    const unselectAllGlobal = () => {
        if (!(hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) &&
            hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT))) {
            return;
        }

        setPermissions([]);
    };

    const groupPermissionsForSave = (flat) => {
        const grouped = {};

        flat.forEach(({ module, action, id }) => {
            if (!grouped[module]) {
                grouped[module] = { module, actions: [] };
            }
            grouped[module].actions.push(action);
            if (id) grouped[module].id = id;
        });

        existingPermissions.forEach((perm) => {
            if (!grouped[perm.module]) {
                grouped[perm.module] = {
                    module: perm.module,
                    actions: [],
                    id: perm.id
                };
            }
        });

        return Object.values(grouped);
    };

    const handleSavePermissions = async () => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const userId = user?.data?.id;

            if (!role_id) {
                toast.error("Role ID missing");
                return;
            }

            const grouped = groupPermissionsForSave(permissions);

            const payload = {
                roleId: Number(role_id),
                permissions: grouped,
                userId
            };

            const res = await dispatch(savePermissions(payload)).unwrap();

            if (res?.success) {
                toast.success(res.message || "Permissions saved");
                loadPermissions(role_id);
            } else {
                toast.error(res?.message || "Failed to save permissions");
            }
        } catch {
            toast.error("Something went wrong");
        }
    };

    const handleReset = () => {
        // reload
        window.location.reload();
    }

    return (
        <Layout>
            <div className="mt-3 px-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button variant="secondary" onClick={() => window.history.back()}>
                        ← Back
                    </Button>

                    <h3 className="fw-semibold m-0">
                        Permissions for: <span className="text-primary">{role?.name}</span>
                    </h3>

                    {
                        hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) && hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT) && <Button variant="primary" className="px-4" onClick={handleSavePermissions}>
                            Save Permissions
                        </Button>
                    }
                </div>

                {
                    (hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) && hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT)) && <div className="d-flex justify-content-end gap-2 mb-2">
                        <Button size="sm" variant="outline-primary" onClick={selectAllGlobal}>
                            Select All
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={unselectAllGlobal}>
                            Unselect All
                        </Button>
                    </div>
                }

                <div className="card shadow-sm">
                    <div className="card-header bg-light fw-bold d-flex justify-content-between align-items-center">
                        <span>Permission Matrix</span>

                        {hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) &&
                            hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT) && (
                                <Button
                                    variant="warning"
                                    size="sm"
                                    onClick={handleReset}
                                >
                                    Reset All
                                </Button>
                            )}
                    </div>


                    <div className="card-body table-responsive">
                        {loading ? (
                            <div className="py-4 text-center">
                                <Spinner animation="border" />
                                <p className="mt-2">Loading permissions...</p>
                            </div>
                        ) : (
                            <Table bordered hover className="align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Module</th>
                                        {ACTIONS.map((a) => (
                                            <th key={a} className="text-center text-capitalize">
                                                {a}
                                            </th>
                                        ))}
                                        {
                                            (hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) && hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT)) && <th className="text-center">Bulk</th>
                                        }
                                    </tr>
                                </thead>

                                <tbody>
                                    {modules.map((m, idx) => (
                                        <tr key={idx}>
                                            <td className="fw-semibold">{m.module}</td>

                                            {ACTIONS.map((action) => (
                                                <td key={action} className="text-center">
                                                    {m.actions.includes(action) ? (
                                                        <div
                                                            className={`d-inline-flex align-items-center justify-content-center cursor-pointer ${hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) && hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT) ? 'hover-effect' : ''}`}
                                                            onClick={() => togglePermission(m.module, action)}
                                                            style={{
                                                                width: '36px',
                                                                height: '36px',
                                                                borderRadius: '50%',
                                                                cursor: hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) && hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT) ? 'pointer' : 'default'
                                                            }}
                                                        >
                                                            {isChecked(m.module, action) ? (
                                                                <FcOk
                                                                    size={28}
                                                                    style={{
                                                                        cursor:
                                                                            hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) &&
                                                                                hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT)
                                                                                ? "pointer"
                                                                                : "not-allowed",
                                                                        filter: "drop-shadow(0 2px 6px rgba(40, 167, 69, 0.4))",
                                                                    }}
                                                                    title="Permission granted"
                                                                />
                                                            ) : (
                                                                <AiFillCloseCircle
                                                                    size={28}
                                                                    color="#ea5455"
                                                                    style={{
                                                                        cursor:
                                                                            hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) &&
                                                                                hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT)
                                                                                ? "pointer"
                                                                                : "not-allowed",
                                                                        filter: "drop-shadow(0 2px 6px rgba(234, 84, 85, 0.4))",
                                                                    }}
                                                                    title="Permission denied"
                                                                />
                                                            )}

                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                            ))}

                                            {
                                                (hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.CREATE) && hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.EDIT)) && <td className="text-center">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-success"
                                                        className="me-1"
                                                        onClick={() =>
                                                            selectAllForModule(m.module, m.actions)
                                                        }
                                                    >
                                                        All
                                                    </Button>

                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() =>
                                                            unselectAllForModule(m.module)
                                                        }
                                                    >
                                                        None
                                                    </Button>
                                                </td>
                                            }
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .hover-effect:hover {
                    background-color: #f8f9fa;
                    transform: scale(1.1);
                    transition: all 0.2s ease;
                }
            `}</style>
        </Layout>
    );
}

export default PermissionManagement;